import { useCallback, useMemo } from "react";
import {
  Alert,
  Button,
  Group,
  Loader,
  Modal,
  Select,
  Stack,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconAlertCircle } from "@tabler/icons-react";

import { useInitialCasePathologistAssignment } from "../../hooks/useInitialCasePathologistAssignment";
import { usePathologists } from "../../hooks/useReferenceData";
import type { AssignPathologistResponse } from "../../types/casePathologistAssignment";
import { getApiErrorStatus } from "../../utils/apiError";

interface AssignPathologistModalProps {
  opened: boolean;
  onClose: () => void;
  caseId: string;
  expectedAssignmentRevision: number;
  onAssigned: (response: AssignPathologistResponse) => void;
}

interface AssignmentFormValues {
  pathologistId: string;
}

const initialValues: AssignmentFormValues = { pathologistId: "" };

export default function AssignPathologistModal({
  opened,
  onClose,
  caseId,
  expectedAssignmentRevision,
  onAssigned,
}: AssignPathologistModalProps) {
  const { canAssignPathologist, assignMutation } =
    useInitialCasePathologistAssignment(caseId);
  const pathologistsQuery = usePathologists({
    enabled: opened && canAssignPathologist,
  });
  const form = useForm<AssignmentFormValues>({
    initialValues,
    validateInputOnBlur: true,
    validate: {
      pathologistId: (value) => (value ? null : "Choose a pathologist."),
    },
  });
  const pathologistOptions = useMemo(
    () =>
      (pathologistsQuery.data ?? []).map((pathologist) => ({
        value: pathologist.id,
        label: pathologist.name,
      })),
    [pathologistsQuery.data]
  );
  const assignmentErrorMessage = useMemo(
    () =>
      getApiErrorStatus(assignMutation.error) === 409
        ? "The pathologist assignment changed. Close this window and review the current case assignment."
        : "The pathologist could not be assigned. Review the case and try again.",
    [assignMutation.error]
  );

  const resetAndClose = useCallback(() => {
    form.reset();
    onClose();
  }, [form, onClose]);

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const validation = form.validate();
      if (validation.hasErrors || assignMutation.isPending) return;
      try {
        const response = await assignMutation.mutateAsync({
          expectedAssignmentRevision,
          pathologistId: form.values.pathologistId,
        });
        onAssigned(response);
        resetAndClose();
      } catch {
        // The modal keeps the selection and renders an actionable error.
      }
    },
    [
      assignMutation,
      expectedAssignmentRevision,
      form,
      onAssigned,
      resetAndClose,
    ]
  );

  return (
    <Modal
      opened={opened}
      onClose={resetAndClose}
      closeOnClickOutside={false}
      closeOnEscape={!assignMutation.isPending}
      closeButtonProps={{ disabled: assignMutation.isPending }}
      title="Assign pathologist"
      size="md"
      centered
      styles={{
        content: {
          maxWidth: "calc(100vw - var(--mantine-spacing-md) * 2)",
          maxHeight: "calc(100dvh - var(--mantine-spacing-md) * 2)",
        },
        body: { overflowY: "auto" },
      }}
    >
      {pathologistsQuery.isLoading ? (
        <Group justify="center" py="xl">
          <Loader aria-label="Loading pathologists" />
        </Group>
      ) : pathologistsQuery.isError ? (
        <Alert
          icon={<IconAlertCircle size={18} />}
          color="red"
          title="Unable to load pathologists"
          role="alert"
        >
          Close this window and try again.
        </Alert>
      ) : !canAssignPathologist ? (
        <Alert
          icon={<IconAlertCircle size={18} />}
          color="red"
          title="Permission required"
          role="alert"
        >
          You do not have permission to assign the case pathologist.
        </Alert>
      ) : (
        <form onSubmit={handleSubmit}>
          <Stack gap="md">
            <Select
              label="Pathologist"
              description="Select the pathologist who should own this case."
              placeholder="Choose a pathologist"
              data={pathologistOptions}
              searchable
              required
              allowDeselect={false}
              disabled={assignMutation.isPending}
              comboboxProps={{
                position: "bottom-start",
                offset: 8,
                withinPortal: true,
              }}
              nothingFoundMessage="No eligible pathologists found"
              {...form.getInputProps("pathologistId")}
            />

            {assignMutation.error && (
              <Alert
                icon={<IconAlertCircle size={18} />}
                color="red"
                title="Pathologist could not be assigned"
                role="alert"
              >
                {assignmentErrorMessage}
              </Alert>
            )}

            <Group justify="flex-end">
              <Button
                type="button"
                variant="default"
                onClick={resetAndClose}
                disabled={assignMutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" loading={assignMutation.isPending}>
                {assignMutation.isPending
                  ? "Assigning pathologist"
                  : "Assign pathologist"}
              </Button>
            </Group>
          </Stack>
        </form>
      )}
    </Modal>
  );
}
